import React from 'react'
import { Title, FormItem, Input } from '../styles'
import {
  Container,
  Form,
  TimePicker,
  DatePicker,
  TextArea,
  Select,
  Option,
  Checkbox,
  HospitalSelectContainer,
  DiseaseInfoContainer,
  DiseaseLabel,
  DiseaseName,
  CustomSelectionDisplay,
  SelectionCode,
  SelectionName,
  SelectionEnglishName,
  OptionContent,
  OptionCode,
  OptionName,
  OptionEnglishName,
} from './styles'
import TreatmentTypeSelect from '../TreatmentTypeSelect'
import { Col, Row } from 'antd'
import OpdRemainText from '../OpdRemainText'
import { datePickerTh } from '@/components/common/BuddhistDatePicker/config'
import { TreatmentDetailPanelHook } from './TreatmentDetailPanel.hook'
import { HospitalOptionType } from './types'
import { useHealthClaimForm } from '@/contexts/healthClaimForm'

function TreatmentDetailPanel() {
  const { markEditedOnce } = useHealthClaimForm()
  const {
    // Form and state
    treatmentDetailForm,
    isCreateOrEditMode,
    isRequired,
    selectedPolicyNumber,
    opdLeftCount,
    healthClaim,
    isAppClaimChannel,

    // Treatment type state - helper functions handle this internally

    // Hospital state
    hospitalOptions,
    isLoadingHospitals,
    isOtherHospital,

    // Disease state
    icd10Options,
    isLoadingDiseases,
    hasMoreDiseases,
    diseaseSearchTerm,
    diseaseInfo,

    // Helper functions
    showAccidentFields,
    showOpdFields,
    showIpdFields,
    showHibFields,
    isTreatmentSelected,

    // Handler functions
    handleTreatmentTypeChange,
    handleHospitalChange,
    handleDiseaseChange,
    handleDiseaseSearch,
    handleDiseaseBlur,
    handleDiseaseDropdownVisibleChange,
    handleDiseasePopupScroll,
    handleOtherHospitalChange,
    handleTreatmentDateInDisabledDate,
    handleTreatmentDateOutDisabledDate,
    handleAccidentDateDisabledDate,
  } = TreatmentDetailPanelHook()
  const userSpecifiedDiseaseName = diseaseInfo.userSpecified.trim()

  return (
    <Form
      form={treatmentDetailForm}
      layout="vertical"
      requiredMark={false}
      onValuesChange={markEditedOnce}
      key={healthClaim.data?.claimFormId}
    >
      <Container>
        <Title>รายละเอียดการเข้ารับการรักษา</Title>

        {/* Treatment Type Selection */}
        <Row gutter={16}>
          <Col className="gutter-row" span={6}>
            <FormItem name="treatmentTypeId" label="ประเภทการรักษา *" rules={[isRequired]}>
              <TreatmentTypeSelect
                policyNumber={selectedPolicyNumber}
                disabled={!isCreateOrEditMode}
                treatmentTypeId={treatmentDetailForm.getFieldValue('treatmentTypeId')}
                onChange={handleTreatmentTypeChange}
              />
            </FormItem>
          </Col>
          {showOpdFields() && (
            <Col className="gutter-row" span={6}>
              <OpdRemainText count={opdLeftCount} />
            </Col>
          )}
        </Row>

        {/* Show fields based on treatment type */}
        {showAccidentFields() && (
          <>
            {/* Accident Location */}
            <Row gutter={16}>
              <Col className="gutter-row" span={6}>
                <FormItem name="accidentLocation" label="สถานที่เกิดเหตุ *" rules={[isRequired]}>
                  <Input type="text" placeholder="ระบุ" disabled={!isCreateOrEditMode} />
                </FormItem>
              </Col>
            </Row>

            {/* Accident Date and Time */}
            <Row gutter={16}>
              <Col className="gutter-row" span={3}>
                <FormItem name="accidentDate" label="วันที่เกิดเหตุ *" rules={[isRequired]}>
                  <DatePicker
                    format="DD/MM/BBBB"
                    locale={datePickerTh}
                    disabled={!isCreateOrEditMode}
                    disabledDate={handleAccidentDateDisabledDate}
                  />
                </FormItem>
              </Col>
              <Col className="gutter-row" span={3}>
                <FormItem name="accidentTime" label="เวลาประมาณ *" rules={[isRequired]}>
                  <TimePicker disabled={!isCreateOrEditMode} />
                </FormItem>
              </Col>
            </Row>
          </>
        )}

        {/* Treatment Dates - Show for all types */}
        {isTreatmentSelected && (
          <>
            <Row gutter={16}>
              <Col className="gutter-row" span={6}>
                <FormItem
                  name="treatmentDateIn"
                  label="วันที่เข้ารับการรักษา *"
                  rules={[isRequired]}
                >
                  <DatePicker
                    format="DD/MM/BBBB"
                    locale={datePickerTh}
                    disabled={!isCreateOrEditMode}
                    disabledDate={handleTreatmentDateInDisabledDate}
                  />
                </FormItem>
              </Col>
              {(showIpdFields() || showHibFields()) && (
                <Col className="gutter-row" span={6}>
                  <FormItem
                    name="treatmentDateOut"
                    label="วันที่ออกจากโรงพยาบาล *"
                    rules={[isRequired]}
                  >
                    <DatePicker
                      format="DD/MM/BBBB"
                      locale={datePickerTh}
                      disabled={!isCreateOrEditMode}
                      disabledDate={handleTreatmentDateOutDisabledDate}
                    />
                  </FormItem>
                </Col>
              )}
            </Row>

            {/* Hospital Selection */}
            <Row gutter={16}>
              <Col className="gutter-row" span={6}>
                <FormItem
                  name="hospitalId"
                  label="สถานพยาบาลในสัญญา *"
                  dependencies={['isOtherHospital']}
                  rules={[
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        const other = getFieldValue('isOtherHospital')
                        if (!other && !value) {
                          return Promise.reject(new Error('กรุณากรอกข้อมูลนี้'))
                        }
                        return Promise.resolve()
                      },
                    }),
                  ]}
                >
                  {(() => {
                    const hospitalId = treatmentDetailForm.getFieldValue('hospitalId')
                    const displayHospital = hospitalId
                      ? hospitalOptions.find((h: HospitalOptionType) => h.value === hospitalId)
                      : null

                    return (
                      <div style={{ position: 'relative' }}>
                        <Select
                          showSearch
                          placeholder={'เลือก'}
                          value={hospitalId}
                          disabled={!isCreateOrEditMode || isLoadingHospitals || isOtherHospital}
                          loading={isLoadingHospitals}
                          className={displayHospital ? 'custom-display hospital-select' : ''}
                          onChange={handleHospitalChange}
                          filterOption={(input, option) => {
                            const hospital = hospitalOptions.find(
                              (h: HospitalOptionType) => h.value === option?.value,
                            )
                            if (!hospital) return false
                            return (
                              hospital.label.toLowerCase().includes(input.toLowerCase()) ||
                              hospital.code.toLowerCase().includes(input.toLowerCase())
                            )
                          }}
                        >
                          {hospitalOptions.map((option: HospitalOptionType) => (
                            <Option key={option.value} value={option.value}>
                              <OptionContent>
                                <OptionCode>{option.code}</OptionCode>
                                <OptionName>{option.fullName}</OptionName>
                              </OptionContent>
                            </Option>
                          ))}
                        </Select>
                        {displayHospital && (
                          <CustomSelectionDisplay>
                            <SelectionCode disabled={!isCreateOrEditMode}>
                              {displayHospital.code}
                            </SelectionCode>
                            <SelectionName disabled={!isCreateOrEditMode}>
                              {displayHospital.fullName}
                            </SelectionName>
                          </CustomSelectionDisplay>
                        )}
                      </div>
                    )
                  })()}
                </FormItem>
              </Col>
              <Col
                className="gutter-row"
                span={12}
                style={{ display: 'flex', alignItems: 'center' }}
              >
                <HospitalSelectContainer>
                  <FormItem name="isOtherHospital" valuePropName="checked" noStyle>
                    <Checkbox disabled={!isCreateOrEditMode} onChange={handleOtherHospitalChange}>
                      ระบุสถานพยาบาลอื่นๆ
                    </Checkbox>
                  </FormItem>
                </HospitalSelectContainer>
              </Col>
            </Row>

            {/* Other Hospital Name - conditional */}
            {isOtherHospital && (
              <Row gutter={16}>
                <Col className="gutter-row" span={12}>
                  <FormItem
                    name="otherHospitalName"
                    label="ชื่อสถานพยาบาล *"
                    dependencies={['isOtherHospital']}
                    rules={[
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          const other = getFieldValue('isOtherHospital')
                          if (other && !value) {
                            return Promise.reject(new Error('กรุณากรอกข้อมูลนี้'))
                          }
                          return Promise.resolve()
                        },
                      }),
                    ]}
                  >
                    <Input type="text" placeholder="ระบุ" disabled={!isCreateOrEditMode} />
                  </FormItem>
                </Col>
              </Row>
            )}

            {/* Disease Information */}
            {isAppClaimChannel && !showAccidentFields() && !!userSpecifiedDiseaseName && (
              <Row gutter={16}>
                <Col className="gutter-row" span={24}>
                  <DiseaseInfoContainer>
                    <DiseaseLabel>ชื่อโรคที่ผู้เอาประกันระบุ</DiseaseLabel>
                    <DiseaseName>{userSpecifiedDiseaseName}</DiseaseName>
                  </DiseaseInfoContainer>
                </Col>
              </Row>
            )}

            {/* ICD10 Selection */}
            <Row gutter={16}>
              <Col className="gutter-row" span={24}>
                <FormItem name="diseaseId" label="ชื่อโรคใน ICD10 *" rules={[isRequired]}>
                  {(() => {
                    const diseaseId = treatmentDetailForm.getFieldValue('diseaseId')
                    const displayDisease = diseaseId
                      ? icd10Options.find((d) => d.value === diseaseId)
                      : null

                    return (
                      <div style={{ position: 'relative' }}>
                        <Select
                          showSearch
                          searchValue={diseaseSearchTerm}
                          placeholder="เลือก"
                          value={diseaseId}
                          disabled={!isCreateOrEditMode}
                          loading={isLoadingDiseases}
                          className={displayDisease ? 'custom-display disease-select' : ''}
                          onChange={handleDiseaseChange}
                          onBlur={handleDiseaseBlur}
                          onSearch={handleDiseaseSearch}
                          onDropdownVisibleChange={handleDiseaseDropdownVisibleChange}
                          onPopupScroll={handleDiseasePopupScroll}
                          filterOption={false} // Disable client-side filtering since we're using server-side search
                          notFoundContent={isLoadingDiseases ? 'กำลังโหลด...' : 'ไม่พบข้อมูล'}
                        >
                          {icd10Options.map((option) => (
                            <Option key={option.value} value={option.value}>
                              <OptionContent>
                                <OptionCode>{option.code}</OptionCode>
                                <OptionName>{option.thaiName}</OptionName>
                                <OptionEnglishName>{option.englishName}</OptionEnglishName>
                              </OptionContent>
                            </Option>
                          ))}
                          {/* Loading indicator for lazy loading */}
                          {isLoadingDiseases && hasMoreDiseases && (
                            <Option key="loading" disabled>
                              <div style={{ textAlign: 'center', padding: '8px' }}>
                                กำลังโหลด...
                              </div>
                            </Option>
                          )}
                        </Select>
                        {displayDisease && (
                          <CustomSelectionDisplay>
                            <SelectionCode disabled={!isCreateOrEditMode}>
                              {displayDisease.code}
                            </SelectionCode>
                            <SelectionName disabled={!isCreateOrEditMode}>
                              {displayDisease.thaiName}
                            </SelectionName>
                            <SelectionEnglishName disabled={!isCreateOrEditMode}>
                              {displayDisease.englishName}
                            </SelectionEnglishName>
                          </CustomSelectionDisplay>
                        )}
                      </div>
                    )
                  })()}
                </FormItem>
              </Col>
            </Row>

            {/* Accident Details - conditional for accident type */}
            {showAccidentFields() && (
              <Row gutter={16}>
                <Col className="gutter-row" span={24}>
                  <FormItem name="accidentDetails" label="ลักษณะการเกิดเหตุ (ถ้ามี)">
                    <TextArea
                      placeholder="เขียนอธิบายลักษณะอาการก่อนเกิดเหตุ"
                      rows={4}
                      disabled={!isCreateOrEditMode}
                    />
                  </FormItem>
                </Col>
              </Row>
            )}
          </>
        )}
      </Container>
    </Form>
  )
}

export default TreatmentDetailPanel
